import {
  Grid,
  Typography,
  Divider,
  Box,
  Paper,
  Button,
  Chip,
  TablePagination,
  Tooltip,
  IconButton,
  TextField,
} from "@mui/material";
import { useIntl } from "react-intl";
import { useEffect, useMemo, useState } from "react";
import DisabledByDefaultIcon from "@mui/icons-material/DisabledByDefault";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import ModalComponent from "components/Modal";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { Spinner } from "react-bootstrap";
import { numericFormatter } from "react-number-format";
import Alert from "@mui/material/Alert";
import env from "react-dotenv";
import {
  IconClockHour4,
  IconBan,
  IconCircleCheck,
  IconEye,
  IconCashRegister,
  IconShoppingBagX,
} from "@tabler/icons-react";
import { BotonIcono } from "shared/botonIcono";

interface Inventario {
  id: number;
  nombre: string;
  clave: string;
}

interface CotizacionDetalle {
  id: number;
  id_cotizacion_proveedor: number;
  disponibilidad: number;
  id_inventario: number;
  equipo: string;
  cantidad: number;
  precio_unitario: number;
  total: number;
  gestionado: number | null;
  seleccionado: number | null;
  recotizado: number | null;
  inventario: Inventario | null;
}

interface Proveedor {
  id: number;
  razon_social: string;
  nombre_comercial: string;
  contacto: string;
  correo: string;
  telefono: string;
}

interface CotizacionProveedor {
  id: number;
  id_cotizacion: number;
  id_proveedor: number;
  subtotal: string;
  total: string;
  notificado: number;
  proveedor: Proveedor;
  detalles: CotizacionDetalle[];
}

interface Empresa {
  id: number;
  clave: string;
  razon_social: string;
  nombre: string;
  telefono: number;
  correo: string;
  logo: string;
}

interface Requisicion {
  id: number;
  folio: string;
  id_solicitud_computo: number;
  id_empresa: number;
  nombre_creador: string;
  nombre_usuario: string;
  created_at: string;
  empresa: Empresa;
}

interface Cotizacion {
  id: number;
  id_requisicion: number;
  fecha: string;
  observaciones: string | null;
  estatus: number;
  created_at: string;
  requisicion: Requisicion;
  proveedores: CotizacionProveedor[];
}

interface CotizacionPorIdProps {
  verCotizacion?: Cotizacion | null;
  detallesCotizacionPaginados: CotizacionDetalle[];
  procesando?: boolean;
  procesandoPrecio?: boolean;
  page: number;
  rowsPerPage: number;
  handleChangePage: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  handleChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  gestionarProducto: (datos: any) => Promise<void>;
  noDisponibleProducto: (datos: any) => Promise<void>;
  handleisAlertOpenPrecio: () => void;
  handleisAlertClosePrecio: () => void;
  isAlertOpenPrecio: boolean;
  onTodosGestionados: (datos: any) => Promise<void>;
}

const estiloEstatus = (d: CotizacionDetalle) => {
  if (d.gestionado !== 1)
    return { color: "#ed6c02", fondo: "#fff8f0", etiqueta: "PENDIENTE DE COTIZAR", Icono: IconClockHour4 };
  if (d.seleccionado === 2)
    return {
      color: "#bd0606",
      fondo: "#fff5f5",
      etiqueta: d.recotizado === 1 ? "RECHAZADO · RECOTIZADO" : "RECHAZADO",
      Icono: IconBan,
    };
  if (d.seleccionado === 3)
    return { color: "#1dad76", fondo: "#f3fbf7", etiqueta: "AUTORIZADO", Icono: IconCircleCheck };
  return { color: "#1d80c2", fondo: "#f4f9fd", etiqueta: "EN REVISIÓN", Icono: IconEye };
};

const CotizacionPorId = ({
  verCotizacion,
  procesandoPrecio,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  detallesCotizacionPaginados,
  gestionarProducto,
  noDisponibleProducto,
  handleisAlertOpenPrecio,
  handleisAlertClosePrecio,
  isAlertOpenPrecio,
  onTodosGestionados,
}: CotizacionPorIdProps) => {
  const intl = useIntl();

  const [precioUnitario, setPrecioUnitario] = useState("");
  const [idProductoProveedor, setIdProductoProveedor] = useState<number>();
  const cotizacionProveedor = verCotizacion?.proveedores?.[0] ?? null;

  // El total ignora los rechazados que ya se mandaron a recotizar
  const totalProveedor = useMemo(() => {
    return (cotizacionProveedor?.detalles ?? [])
      .filter((d) => !(d.seleccionado === 2 && d.recotizado === 1))
      .reduce((acc, d) => acc + d.precio_unitario * d.cantidad, 0);
  }, [cotizacionProveedor]);

  const todosGestionados = useMemo(() => {
    const detalles = cotizacionProveedor?.detalles ?? [];
    return detalles.length > 0 && detalles.every((d) => d.gestionado === 1);
  }, [cotizacionProveedor]);

  const estatusProveedor = useMemo(() => {
    const detalles = cotizacionProveedor?.detalles ?? [];
    if (detalles.length === 0) return { label: "Pendiente", color: "warning" as const };
    if (detalles.some((d) => d.gestionado !== 1))
      return { label: "Pendiente", color: "warning" as const };
    if (detalles.some((d) => d.seleccionado === 3))
      return { label: "Autorizado ✓", color: "success" as const };
    return { label: "Cotizado", color: "info" as const };
  }, [cotizacionProveedor]);

  useEffect(() => {
    if (todosGestionados) {
      const datos = {
        id_solicitud_computo: verCotizacion?.requisicion?.id_solicitud_computo,
        nombre_proveedor: cotizacionProveedor?.proveedor?.nombre_comercial,
        id_proveedor: cotizacionProveedor?.id_proveedor,
        id_cotizacion: cotizacionProveedor?.id_cotizacion,
        productos_cotizados: cotizacionProveedor?.detalles,
        id_cotizacion_proveedor: cotizacionProveedor?.id,
      };
      cotizacionProveedor?.notificado === 1 ? null : onTodosGestionados(datos);
    }
  }, [todosGestionados]);

  if (!verCotizacion) return null;

  return (
    <Box sx={{ px: 2, pb: 2, mt: 3 }}>
      <Grid container spacing={2}>
        {/* Header */}
        <Grid item xs={12}>
          <Typography variant="h4" sx={{ textAlign: "center" }}>
            Solicitud de cotización
          </Typography>
          {verCotizacion.requisicion?.folio && (
            <Typography variant="subtitle1" sx={{ textAlign: "center" }} color="text.secondary">
              Requisición {verCotizacion.requisicion.folio}
            </Typography>
          )}
        </Grid>

        {/* Info de la empresa */}
        {verCotizacion.requisicion?.empresa && (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography
                variant="h6"
                color="primary"
                gutterBottom
                sx={{ color: "#004c93", textAlign: "center" }}
              >
                Empresa solicitante
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2} alignItems="center">

                <Grid item xs={12} md={3} sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  {verCotizacion.requisicion.empresa.logo && (
                    <Box
                      component="img"
                      src={`${env.API_URL_ASSETS}logo_empresas/${verCotizacion.requisicion.empresa.logo}`}
                      alt="Logo empresa"
                      sx={{ width: 150, height: "auto" }}
                    />
                  )}
                  <Typography variant="h5" fontWeight="medium">
                    {verCotizacion.requisicion.empresa.razon_social}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={2}>
                  <Typography variant="body2" color="text.secondary">
                    Nombre
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {verCotizacion.requisicion.empresa.nombre}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="body2" color="text.secondary">
                    Teléfono
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {verCotizacion.requisicion.empresa.telefono}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Correo
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {verCotizacion.requisicion.empresa.correo}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Info del proveedor */}
        {cotizacionProveedor && (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography
                variant="h6"
                color="primary"
                gutterBottom
                sx={{ color: "#004c93", textAlign: "center" }}
              >
                Datos del proveedor
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Nombre comercial
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {cotizacionProveedor.proveedor.nombre_comercial}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Razón Social
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {cotizacionProveedor.proveedor.razon_social}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Contacto
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {cotizacionProveedor.proveedor.contacto || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Correo
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {cotizacionProveedor.proveedor.correo || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Teléfono
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {cotizacionProveedor.proveedor.telefono || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Estatus
                  </Typography>
                  <Chip
                    label={estatusProveedor.label}
                    color={estatusProveedor.color}
                    size="small"
                    sx={{ fontWeight: "bold" }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {cotizacionProveedor && (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography
                variant="h6"
                color="primary"
                gutterBottom
                sx={{ color: "#004c93", textAlign: "center" }}
              >
                Productos solicitados ({cotizacionProveedor.detalles?.length ?? 0})
              </Typography>
              <Typography
                variant="caption"
                color="info"
                sx={{ display: "block", textAlign: "center" }}
              >
                Por favor utiliza los botones de acciones para registrar precio o no disponibilidad
                del producto solicitado
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {todosGestionados && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  ✅ Todos los productos han sido gestionados
                </Alert>
              )}

              {detallesCotizacionPaginados?.map((detalle) => {
                const esComputo = !!verCotizacion?.requisicion?.id_solicitud_computo;
                const nombreProducto = esComputo
                  ? detalle?.equipo
                  : detalle.inventario?.nombre ?? "—";
                const extraProducto = esComputo ? "—" : detalle.inventario?.clave;
                const est = estiloEstatus(detalle);
                const pendiente = detalle.gestionado !== 1;

                return (
                  <Box
                    key={detalle.id}
                    sx={{
                      position: "relative",
                      mb: 2,
                      p: 1.5,
                      pt: 4,
                      border: `2px solid ${est.color}`,
                      borderRadius: 1,
                      backgroundColor: est.fondo,
                      overflow: "hidden",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        display: "flex",
                        alignItems: "center",
                        color: "#fff",
                        backgroundColor: est.color,
                        p: 0.5,
                        px: 1,
                        fontSize: 10,
                        fontWeight: 600,
                        borderBottomRightRadius: 6,
                      }}
                    >
                      <est.Icono stroke={2} size={14} style={{ marginRight: 4 }} />
                      {est.etiqueta}
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={pendiente ? 4 : 6}>
                        <Typography variant="body2" color="text.secondary">
                          Producto
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {nombreProducto}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {extraProducto}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={1}>
                        <Typography variant="body2" color="text.secondary">
                          Cantidad
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {detalle.cantidad}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="text.secondary">
                          P. Unitario
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {numericFormatter(detalle.precio_unitario + "", {
                            thousandSeparator: ",",
                            decimalScale: 2,
                            fixedDecimalScale: true,
                            prefix: "$",
                          })}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="text.secondary">
                          Total
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          color="primary"
                          sx={{ color: "#004c93" }}
                        >
                          {numericFormatter(detalle.precio_unitario * detalle.cantidad + "", {
                            thousandSeparator: ",",
                            decimalScale: 2,
                            fixedDecimalScale: true,
                            prefix: "$",
                          })}
                        </Typography>
                      </Grid>
                      {pendiente && detalle.disponibilidad !== null && (
                        <Grid item xs={6} md={1}>
                          <Typography variant="body2" color="text.secondary">
                            Disponible
                          </Typography>
                          <Chip
                            label={detalle.disponibilidad === 1 ? "Sí" : "No"}
                            color={detalle.disponibilidad === 1 ? "success" : "error"}
                            size="small"
                          />
                        </Grid>
                      )}
                      {pendiente && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">
                            Acciones
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <BotonIcono
                              onClick={() => {
                                setIdProductoProveedor(detalle?.id);
                                handleisAlertOpenPrecio();
                              }}
                              icono={<IconCashRegister stroke={2} />}
                              textoUno="Registrar"
                              textoDos="Precio"
                              colorTexto="#fff"
                              fondoBoton="#0d9177"
                              fondoHover="#0a7360"
                              degradado
                            />
                            <BotonIcono
                              onClick={() => {
                                noDisponibleProducto({ id_cotizacion_detalle: detalle?.id });
                              }}
                              icono={<IconShoppingBagX stroke={2} />}
                              textoUno="Sin"
                              textoDos="disponibilidad"
                              colorTexto="#fff"
                              fondoBoton="#941316"
                              fondoHover="#de1b1f"
                              degradado
                            />
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                );
              })}

              {/* Total */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  mt: 1,
                  p: 1,
                  backgroundColor: "#e8f4fd",
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" fontWeight="bold" sx={{ mr: 1 }}>
                  Total de la cotización:
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="primary">
                  {numericFormatter(totalProveedor + "", {
                    thousandSeparator: ",",
                    decimalScale: 2,
                    fixedDecimalScale: true,
                    prefix: "$",
                  })}
                </Typography>
              </Box>

              {/* Paginación */}
              <TablePagination
                component="div"
                count={cotizacionProveedor.detalles?.length ?? 0}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[3, 5, 10]}
                labelRowsPerPage="Por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{
                  ".MuiTablePagination-toolbar": {
                    justifyContent: "space-between",
                    padding: "8px 16px",
                  },
                  ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                    margin: 0,
                  },
                  ".MuiTablePagination-actions svg": { transform: "scaleX(-1)" },
                }}
              />
            </Paper>
          </Grid>
        )}
      </Grid>
      <ModalComponent
        handleClose={handleisAlertClosePrecio}
        isOpen={isAlertOpenPrecio}
        key={"alerta-precio"}
      >
        <Grid container spacing={2} style={{ textAlign: "center" }}>
          <Grid item xs={12}>
            <br />
            <br />
            <Typography variant="h6" color="primary" gutterBottom sx={{ textAlign: "center" }}>
              Registrar precio del producto
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ position: "relative" }}>
            <TextField
              id="precio_unitario"
              fullWidth
              label={`${intl.formatMessage({ id: "input_precio_unitario" })}`}
              variant="standard"
              name="precio_unitario"
              type="number"
              value={precioUnitario || ""}
              onChange={(e) => setPrecioUnitario(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Button
              sx={{ color: "#fff", background: "#084d6e" }}
              variant="contained"
              endIcon={<AddBoxIcon />}
              disabled={procesandoPrecio}
              onClick={() => {
                gestionarProducto({
                  id_cotizacion_detalle: idProductoProveedor,
                  precio_unitario: precioUnitario,
                });
              }}
            >
              {procesandoPrecio ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  {intl.formatMessage({ id: "general_registrando" })}
                  ...{" "}
                </>
              ) : (
                intl.formatMessage({ id: "general_registrar" })
              )}
            </Button>
          </Grid>
        </Grid>
      </ModalComponent>
    </Box>
  );
};

export default CotizacionPorId;